import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Box,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ImagenPerfil from "../../assets/ImagenPerfilTransmex.png";
import { useLocation } from "react-router-dom";
import { GET, PUT, POST } from "../Generic/CrudRequest";
import { EmployeeEdit } from "./EmployeeEdit";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Alerts from "../../Utils/Alerts";
import Icons from "../../Utils/Icons";

export default function AddOrEditEmployee({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [employeeData, setEmployeeData] = useState(new EmployeeEdit());
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [locationEmployee, setLocationEmployee] = useState(null);
  const [userPhoto, setUserPhoto] = useState("");
  const [fecha, setFecha] = useState(dayjs(new Date()));
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  dayjs.extend(utc);

  useEffect(() => {
    if (location.pathname.includes("/Empleados/Editar")) {
      setTitle(t("employees.edit"));
      const employeeId = getUserIdFromPath(location.pathname);
      getEmployeeId(employeeId);
    } else if (location.pathname.includes("/Empleados/Agregar")) {
      setTitle(t("employees.add"));
      setUserPhoto(ImagenPerfil);
    }
  }, [location.pathname, t]);

  useEffect(() => {
    if (title === t("employees.edit") && employeeData.idUbicacion !== 0) {
      setLocationEmployee({
        idUbicacion: employeeData.idUbicacion,
        nombre: employeeData.nombreUbicacion,
        descripcion: "",
        ciudad: "",
        ubicacionTipo: "",
      });
    }
  }, [title, employeeData]);

  const fetchLocations = async (location, currentPage, perPage) => {
    const dataLocations = await GET(`${config.url}/api/Location/select`, {
      busqueda: location,
      page: currentPage,
      perPage: perPage,
    });

    setLocations(dataLocations.result);
  };

  const getUserIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getEmployeeId = async (employeeId) => {
    const dataEmployee = await GET(`${config.url}/api/Employes/${employeeId}`);
    const updatedEmployeeData = new EmployeeEdit(dataEmployee);

    setFecha(dayjs.utc(updatedEmployeeData.fechaIngreso).local());
    setEmployeeData(updatedEmployeeData);

    if (updatedEmployeeData.idUbicacion !== 0) {
      const locationData = await GET(
        `${config.url}/api/Location/${updatedEmployeeData.idUbicacion}`
      );
      fetchLocations(locationData.nombre, 1, 1);
    }

    findImage(updatedEmployeeData.foto);
  };

  const findImage = (image) => {
    if (image !== "") {
      const imagePath = `${config.url}/${config.paths.employeeImage}/${image}`;
      setUserPhoto(imagePath);
    } else {
      setUserPhoto(ImagenPerfil);
    }
  };

  const addEmployee = async () => {
    const requiredFields = ["nombre", "noEmpleado", "apellidoPaterno"];
    const newErrors = {};

    if (
      employeeData.correo.trim() !== "" &&
      !validateEmail(employeeData.correo)
    ) {
      newErrors.correo = t("employees.invalidEmail");
    }

    if (employeeData.idUbicacion === 0) {
      newErrors.ubicacion = t("employees.requiredField");
    }

    if (employeeData.fechaIngreso === "Invalid Date") {
      employeeData.fechaIngreso = "";
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(employeeData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("employees.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    employeeData.fechaIngreso = fecha.format("MM-DD-YYYY HH:mm:ss");

    await POST(`${config.url}/api/Employes`, employeeData);
    navigate("/Empleados");
  };

  const editEmployee = async () => {
    const requiredFields = ["nombre", "noEmpleado", "apellidoPaterno"];
    const newErrors = {};

    if (
      employeeData.correo.trim() !== "" &&
      !validateEmail(employeeData.correo)
    ) {
      newErrors.correo = t("employees.invalidEmail");
    }

    if (employeeData.idUbicacion === 0) {
      newErrors.ubicacion = t("employees.requiredField");
    }

    if (employeeData.fechaIngreso === "Invalid Date") {
      employeeData.fechaIngreso = "";
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(employeeData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("employees.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    employeeData.fechaIngreso = fecha.format("MM-DD-YYYY HH:mm:ss");

    await PUT(`${config.url}/api/Employes`, employeeData);
    navigate("/Empleados");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleSubmit = async () => {
    if (title === t("employees.add")) {
      await addEmployee();
    } else if (title === t("employees.edit")) {
      await editEmployee();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData({
      ...employeeData,
      [name]: value,
    });

    if (name === "correo" && value.trim() !== "") {
      if (!validateEmail(value)) {
        setErrors({
          ...errors,
          [name]: t("employees.invalidEmail"),
        });
      } else {
        setErrors({
          ...errors,
          [name]: "",
        });
      }
    }
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleShowAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        handleShowAlert(t("employees.onlyImagesAllowed"), "warning");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setUserPhoto(reader.result);
        const base64Image = reader.result.split(",")[1];
        const extension = file.name.split(".").pop();

        setEmployeeData({
          ...employeeData,
          base64: base64Image,
          extension: extension,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocations(value, 1, 10);
    } else {
      setLocations([]);
    }
  };

  const handleNumberInput = (e) => {
    const key = e.key;

    if (
      !/^[0-9]$/.test(key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key)
    ) {
      e.preventDefault();
    }
  };

  return (
    <>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <CardContent
            sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
          >
            <Icons
              iconName="ArrowBack"
              fontSize="large"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                marginRight: 20,
              }}
              to="/Empleados"
              tooltip={t("buttons.back")}
            />
            <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
              {title} {t("employees.title")}
            </Typography>
          </CardContent>
          <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("employees.employeeInfo")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    maxWidth: "calc(100% - 64px)",
                    margin: "auto",
                    marginTop: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    component="img"
                    sx={{
                      maxWidth: "100%", // Utilizando porcentaje para ajustarse al contenedor
                      maxHeight: "auto", // Para mantener la proporción de la imagen
                      borderRadius: "50%",
                      objectFit: "cover",
                      margin: 4,
                    }}
                    alt={t("navigation.profileAlt")}
                    src={userPhoto}
                  />
                  <input
                    accept="image/*"
                    id="button-file"
                    av="true"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handlePhotoChange}
                  />
                  <label htmlFor="button-file">
                    <Button
                      variant="outlined"
                      component="span"
                      sx={{ maxWidth: 260 }}
                    >
                      {t("employees.selectPhoto")}
                    </Button>
                  </label>
                </Grid>
                <Grid item xs={12} md={8} marginTop={4}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.employeeNumber")}
                        name="noEmpleado"
                        value={employeeData?.noEmpleado || ""}
                        onChange={handleChange}
                        onKeyDown={handleNumberInput}
                        variant="standard"
                        fullWidth
                        error={!!errors.noEmpleado}
                        helperText={errors.noEmpleado}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={locations}
                        noOptionsText={t("noOptions")}
                        value={locationEmployee}
                        getOptionLabel={(location) =>
                          `${location.nombre} ${location.descripcion} ${
                            location.ciudad ?? ""
                          } ${location.ubicacionTipo ?? ""}`
                        }
                        //getOptionValue={(location) => `${location.descripcion}`}
                        onChange={(event, value) => {
                          if (value) {
                            setLocationEmployee(value);
                            setEmployeeData({
                              ...employeeData,
                              nombreUbicacion: value.nombre,
                              idUbicacion: value.idUbicacion,
                            });
                          } else {
                            setEmployeeData({
                              ...employeeData,
                              nombreUbicacion: "",
                              idUbicacion: 0,
                            });
                          }
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option &&
                          value &&
                          option.idUbicacion === value.idUbicacion
                        }
                        onInputChange={handleInputChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("employees.location")}
                            variant="standard"
                            fullWidth
                            value={locationEmployee}
                            error={!!errors.ubicacion}
                            helperText={errors.ubicacion}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.name")}
                        name="nombre"
                        value={employeeData?.nombre || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                        error={!!errors.nombre}
                        helperText={errors.nombre}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.lastName")}
                        name="apellidoPaterno"
                        value={employeeData?.apellidoPaterno || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                        error={!!errors.apellidoPaterno}
                        helperText={errors.apellidoPaterno}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.motherLastName")}
                        name="apellidoMaterno"
                        value={employeeData?.apellidoMaterno || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.nicknames")}
                        name="apodos"
                        value={employeeData?.apodos || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.email")}
                        name="correo"
                        value={employeeData?.correo || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                        error={!!errors.correo}
                        helperText={errors.correo}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.cellphone")}
                        name="telefono"
                        value={employeeData?.telefono || ""}
                        onChange={handleChange}
                        onKeyDown={handleNumberInput}
                        variant="standard"
                        fullWidth
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.street")}
                        name="calle"
                        value={employeeData?.calle || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.neighborhood")}
                        name="colonia"
                        value={employeeData?.colonia || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.exteriorNumber")}
                        name="numeroExterior"
                        value={employeeData?.numeroExterior || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                        inputProps={{
                          maxLength: 10,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.interiorNumber")}
                        name="numeroInterior"
                        value={employeeData?.numeroInterior || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.postalCode")}
                        name="codigoPostal"
                        value={employeeData?.codigoPostal || ""}
                        onChange={handleChange}
                        onKeyDown={handleNumberInput}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={t("employees.city")}
                        name="ciudad"
                        value={employeeData?.ciudad || ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label={t("employees.entryDate")}
                          value={fecha}
                          onChange={(newValue) => {
                            setFecha(dayjs(newValue));
                          }}
                          slots={{
                            textField: (params) => (
                              <TextField
                                variant="standard"
                                fullWidth
                                {...params}
                              />
                            ),
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  {t("employees.save")}
                </Button>
              </Grid>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </>
  );
}
